"use client";

import { motion } from "framer-motion";
import { MapPin, UserCheck, Wind, Baby, MessageSquareText, ShieldCheck } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/a11y";
interface TrustPoint {
  id: string;
  title: string;
  desc: string;
  icon: typeof MapPin;
}
const trustPoints: TrustPoint[] = [{
  id: "door-to-door",
  title: "Private Door-to-Door Service",
  desc: "Direct private transfer to your hotel or resort.",
  icon: MapPin
}, {
  id: "professional-drivers",
  title: "Professional Local Drivers",
  desc: "Friendly local drivers for a smooth journey.",
  icon: UserCheck
}, {
  id: "clean-ac",
  title: "Clean Air-Conditioned Vehicles",
  desc: "Comfortable vehicles for airport and city transfers.",
  icon: Wind
}, {
  id: "child-seats",
  title: "Child Seats on Request",
  desc: "Request a child seat when booking.",
  icon: Baby
}, {
  id: "clear-comm",
  title: "Clear Communication",
  desc: "WhatsApp support and clear booking updates.",
  icon: MessageSquareText
}, {
  id: "no-hidden-fees",
  title: "No Hidden Fees",
  desc: "Fixed prices and payment on arrival.",
  icon: ShieldCheck
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
export default function TrustBar() {
  return <section id="trust-bar" data-nav="light" className="relative border-b border-border bg-background pt-12 pb-12 lg:pt-16 lg:pb-16 text-foreground">
      {/* Soft gradient fade replacing the hard top border */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-foreground/[0.04] to-transparent dark:from-background" aria-hidden="true" />

      <div className="mx-auto max-w-[1136px] px-6">
        {/* Mobile / Tablet Swipeable Swiper Carousel (< md) */}
        <div className="block md:hidden">
          <Swiper modules={[FreeMode, A11y]} freeMode={true} slidesPerView={1.2} spaceBetween={18} breakpoints={{
          640: {
            slidesPerView: 2.1,
            spaceBetween: 20
          }
        }} a11y={{
          prevSlideMessage: "Previous trust point",
          nextSlideMessage: "Next trust point"
        }} className="overflow-visible">
            {trustPoints.map((item, index) => {
            const IconComponent = item.icon;
            return <SwiperSlide key={item.id} data-index={index} className="h-auto">
                  <div className="group flex h-full flex-col items-start gap-3.5 rounded-lg border border-border/60 bg-card p-5 shadow-xs">
                    <div className="flex size-11 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary transition-[color,background-color] duration-200 group-hover:bg-primary group-hover:text-primary-foreground" aria-hidden="true">
                      <IconComponent className="size-5" strokeWidth={1.75} />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-black text-base tracking-tight leading-snug">
                          {item.title}
                        </h3>
                        <span className="size-1.5 shrink-0 rounded-full bg-[var(--chart-3)]" aria-hidden="true" />
                      </div>
                      <p className="text-foreground/80 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>;
          })}
          </Swiper>
        </div>

        {/* Desktop & Tablet Grid (md and above) */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10 lg:gap-x-12 lg:gap-y-12">
          {trustPoints.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} variants={itemVariants} className="group flex flex-col items-start gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary transition-[color,background-color] duration-200 group-hover:bg-primary group-hover:text-primary-foreground" aria-hidden="true">
                  <IconComponent className="size-5" strokeWidth={1.75} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-black text-base lg:text-lg tracking-tight leading-snug">
                      {item.title}
                    </h3>
                    <span className="size-1.5 shrink-0 rounded-full bg-[var(--chart-3)]" aria-hidden="true" />
                  </div>
                  <p className="text-foreground/80 text-sm lg:text-base leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}